import { useState } from 'react';
import { FileText, Loader2, Printer, FileSpreadsheet, FileDown, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface ExportButtonProps {
  reportType: string;
  reportTitle?: string;
  filters?: Record<string, string>;
  data?: any[];
  columns?: string[];
  disabled?: boolean;
}

export default function ExportButton({ 
  reportType, 
  reportTitle,
  filters = {}, 
  data,
  columns,
  disabled 
}: ExportButtonProps) {
  const { toast } = useToast();
  const [exporting, setExporting] = useState<string | null>(null);

  const downloadFile = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportCSV = async () => {
    if (exporting) return;
    setExporting('csv');
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/reports/export/${reportType}?${params.toString()}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      downloadFile(blob, `${reportType}-report-${new Date().toISOString().split('T')[0]}.csv`);
      toast({ title: 'Export Complete', description: 'CSV file downloaded' });
    } catch (error) {
      console.error('Export failed:', error);
      toast({ title: 'Export Failed', description: 'Could not export CSV', variant: 'destructive' });
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting('excel');
    try {
      const response = await api.post('/reports/export/excel', {
        reportType,
        title: reportTitle || reportType,
        data,
        columns,
        filters,
      }, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      downloadFile(blob, `${reportType}-report-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast({ title: 'Export Complete', description: 'Excel file downloaded' });
    } catch (error) {
      console.error('Export failed:', error);
      toast({ title: 'Export Failed', description: 'Could not export Excel', variant: 'destructive' });
    } finally {
      setExporting(null);
    }
  };

  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting('pdf');
    try {
      const response = await api.post('/reports/export/pdf', {
        reportType,
        title: reportTitle || reportType,
        data,
        columns,
        filters,
      }, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      downloadFile(blob, `${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`);
      toast({ title: 'Export Complete', description: 'PDF file downloaded' });
    } catch (error) {
      console.error('Export failed:', error);
      toast({ title: 'Export Failed', description: 'Could not export PDF', variant: 'destructive' });
    } finally {
      setExporting(null);
    }
  };

  const handleExportJSON = async () => {
    if (exporting) return;
    setExporting('json');
    try {
      const response = await api.post('/reports/export/json', {
        reportType,
        title: reportTitle || reportType,
        data,
        columns,
        filters,
      }, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/json' });
      downloadFile(blob, `${reportType}-report-${new Date().toISOString().split('T')[0]}.json`);
      toast({ title: 'Export Complete', description: 'JSON file downloaded' });
    } catch (error) {
      console.error('Export failed:', error);
      toast({ title: 'Export Failed', description: 'Could not export JSON', variant: 'destructive' });
    } finally {
      setExporting(null);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex gap-2 print:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={disabled || !!exporting}
            variant="outline"
            className="gap-2"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            Export
            <ChevronDown className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleExportCSV}>
            <FileText className="w-4 h-4 mr-2" />
            Export as CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleExportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export as Excel
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-2 text-red-500" />
            Export as PDF
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleExportJSON}>
            <FileText className="w-4 h-4 mr-2 text-blue-500" />
            Export as JSON
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <Button
        onClick={handlePrint}
        disabled={disabled}
        variant="outline"
        className="gap-2"
      >
        <Printer className="w-4 h-4" />
        Print
      </Button>
    </div>
  );
}
